import { createContextualCan, useAbility } from '@casl/react';
import { ComponentType, Consumer, Context, JSX, ReactNode, createContext } from 'react';

import { AppAbility, definePermissions } from './define-permissions';

import NotSufficientPermissions from '@/components/atoms/not-sufficient-permissions';
import { UserRole } from '@/types/user-role';

export const AbilityContext = createContext<AppAbility | null>(null);

export const AbilityProvider = ({
  children,
  role,
  userId,
  isSSO,
}: {
  children: ReactNode;
  role: UserRole;
  userId: string;
  isSSO: boolean;
}) => {
  const ability = definePermissions(role, userId, isSSO);

  return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>;
};

export const Can = createContextualCan(AbilityContext.Consumer as Consumer<AppAbility>);

export const usePermissions = () => {
  const ability = useAbility(AbilityContext as Context<AppAbility>);

  if (!ability) {
    throw new Error('usePermissions must be used within a AbilityProvider.');
  }

  return ability;
};

export const withPermissions = <T extends JSX.IntrinsicAttributes>(
  shouldShowNotSufficientPermissions: (permissions: AppAbility) => boolean,
  Component: ComponentType<T>,
) => {
  return (props: T) => {
    const permissions = usePermissions();

    const showNotSufficientPermissions = shouldShowNotSufficientPermissions(permissions);

    if (showNotSufficientPermissions) {
      return <NotSufficientPermissions />;
    }

    return <Component {...props} />;
  };
};
